import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, ArrowLeft, Sparkles, MoreHorizontal, Plus, Clock, BookOpen, Check, Pencil } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useChatSession } from "@/hooks/useChatSession";
import { ChatMessage, ThinkingDots } from "@/components/ChatMessage";

export default function Chat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { convo, messages, streaming, streamText, ending, sendMessage, endSession, renameConversation } =
    useChatSession(id);
  const [input, setInput] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const scrollRef = useRef(null);
  const taRef = useRef(null);

  const startRename = () => {
    setTitleDraft(convo?.title || "");
    setEditingTitle(true);
  };

  const commitRename = () => {
    setEditingTitle(false);
    if (titleDraft.trim() && titleDraft.trim() !== convo?.title) {
      renameConversation(titleDraft);
    }
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamText, streaming]);

  const autoGrow = () => {
    const ta = taRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 200) + "px";
    }
  };

  const submit = () => {
    const content = input.trim();
    if (!content || streaming) return;
    setInput("");
    if (taRef.current) taRef.current.style.height = "auto";
    sendMessage(content);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const empty = messages.length === 0 && !streaming;
  const lastRole = messages[messages.length - 1]?.role;
  // A quiet, in-conversation invitation to save — appears once the reflection
  // has some depth (after the 6th message), reads as part of the conversation.
  const showSavePrompt = messages.length >= 6 && !streaming && !ending && lastRole === "assistant";

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[rgba(26,26,46,0.7)] border-b border-[rgba(242,236,228,0.08)]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button
            data-testid="chat-back-button"
            onClick={() => navigate("/begin")}
            className="flex items-center gap-1.5 text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500 text-xs tracking-[0.15em] uppercase"
          >
            <ArrowLeft size={15} /> Leave
          </button>
          <span className="font-serif-display text-lg font-light text-[var(--mirror-text)] truncate max-w-[45%]">
            {editingTitle ? (
              <input
                data-testid="chat-title-input"
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setEditingTitle(false);
                }}
                className="bg-transparent border-b border-[rgba(242,236,228,0.3)] outline-none text-center w-full text-[var(--mirror-text)] font-serif-display"
              />
            ) : (
              <button
                data-testid="chat-title"
                onClick={startRename}
                className="truncate max-w-full hover:opacity-70 transition-opacity duration-500"
                title="Rename conversation"
              >
                {convo?.title || "Mirror"}
              </button>
            )}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="chat-menu-button"
                className="text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500"
                aria-label="Conversation menu"
              >
                <MoreHorizontal size={20} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#22223B] border border-[rgba(242,236,228,0.12)] text-[var(--mirror-text-2)] min-w-[220px]"
            >
              <DropdownMenuItem
                data-testid="menu-rename"
                onClick={startRename}
                className="gap-2 focus:bg-[rgba(242,236,228,0.06)] focus:text-[var(--mirror-text)] cursor-pointer"
              >
                <Pencil size={15} /> Rename conversation
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="menu-new-conversation"
                onClick={() => navigate("/begin")}
                className="gap-2 focus:bg-[rgba(242,236,228,0.06)] focus:text-[var(--mirror-text)] cursor-pointer"
              >
                <Plus size={15} /> New conversation
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="menu-history"
                onClick={() => navigate("/history")}
                className="gap-2 focus:bg-[rgba(242,236,228,0.06)] focus:text-[var(--mirror-text)] cursor-pointer"
              >
                <Clock size={15} /> Chat history
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="menu-journey"
                onClick={() => navigate("/journey")}
                className="gap-2 focus:bg-[rgba(242,236,228,0.06)] focus:text-[var(--mirror-text)] cursor-pointer"
              >
                <BookOpen size={15} /> Your Journey
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[rgba(242,236,228,0.1)]" />
              <DropdownMenuItem
                data-testid="chat-end-session-button"
                disabled={ending || messages.length < 2}
                onClick={endSession}
                className="gap-2 text-[var(--mirror-accent)] focus:bg-[rgba(242,236,228,0.06)] focus:text-[var(--mirror-accent)] cursor-pointer"
              >
                <Check size={15} /> {ending ? "Saving…" : "Save to your Journey"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
          {empty && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2 }}
              className="text-center mt-16"
              data-testid="chat-empty-state"
            >
              <Sparkles size={20} className="mx-auto text-[var(--mirror-accent)]" />
              <p className="mt-6 font-serif-display text-2xl font-light text-[var(--mirror-text)]">
                What belief are you carrying today?
              </p>
              <p className="mt-2 text-sm text-[var(--mirror-text-3)]">
                There are no wrong answers, only starting points.
              </p>
            </motion.div>
          )}

          <div className="flex flex-col gap-8">
            {messages.map((m) => (
              <ChatMessage key={m.id} m={m} />
            ))}

            {showSavePrompt && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.4 }}
                data-testid="chat-inline-save-prompt"
                className="text-[var(--mirror-text-3)] text-base leading-relaxed font-light italic"
              >
                When you're ready, you can{" "}
                <button
                  data-testid="chat-inline-save-button"
                  onClick={endSession}
                  disabled={ending}
                  className="not-italic text-[var(--mirror-accent)] underline underline-offset-4 decoration-[var(--mirror-accent)]/40 hover:decoration-[var(--mirror-accent)] transition-colors duration-500 disabled:opacity-50"
                >
                  save this to your Journey
                </button>
                .
              </motion.p>
            )}

            <AnimatePresence>
              {streaming && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                >
                  <div className="max-w-full">
                    {streamText ? (
                      <p className="text-[var(--mirror-text)] text-base sm:text-lg leading-relaxed whitespace-pre-wrap font-light">
                        {streamText}
                      </p>
                    ) : (
                      <ThinkingDots />
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 backdrop-blur-xl bg-[rgba(26,26,46,0.7)] border-t border-[rgba(242,236,228,0.08)]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-end gap-3 rounded-3xl border border-[rgba(242,236,228,0.12)] px-5 py-3 focus-within:border-[rgba(242,236,228,0.3)] transition-colors duration-500">
            <textarea
              ref={taRef}
              data-testid="chat-input"
              rows={1}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                autoGrow();
              }}
              onKeyDown={onKey}
              placeholder="Say what is on your mind…"
              className="flex-1 bg-transparent outline-none resize-none text-[var(--mirror-text)] text-base leading-relaxed max-h-[200px] py-1"
            />
            <button
              data-testid="chat-send-button"
              onClick={submit}
              disabled={!input.trim() || streaming}
              className="shrink-0 h-9 w-9 rounded-full border border-[rgba(242,236,228,0.2)] flex items-center justify-center hover:bg-[rgba(242,236,228,0.08)] transition-colors duration-500 disabled:opacity-30"
              aria-label="Send"
            >
              <ArrowUp size={16} />
            </button>
          </div>
          <p className="mt-2.5 text-center text-[11px] text-[var(--mirror-text-3)]">
            Saved automatically · leave and return anytime · save to your Journey from the menu
          </p>
          <p className="mt-1 text-center text-[11px] text-[var(--mirror-accent)] opacity-80">
            Quietly grounded in the world's wisdom traditions.
          </p>
        </div>
      </div>
    </div>
  );
}
